<template>
  <div class="container">
    <b-modal
      ref="bill-attach-modal"
      hide-footer
      :titleHtml="modalTitle"
      size="xl"
    >
      <v-form ref="form">
        <b-alert v-if="apiWarnings.length" show variant="warning" class="mb-3">
          <div v-for="(warning, idx) in apiWarnings" :key="idx">{{ warning }}</div>
        </b-alert>
        <b-alert v-if="isBundleMode" show variant="info" class="mb-4">
          <div class="d-flex flex-column">
            <strong class="mb-1">Ưu đãi combo phụ kiện</strong>
            <span>
              Mua từ {{ bundleMinQty }} phụ kiện, giảm
              {{ bundleDiscountDisplay }}
              trên tổng phụ kiện ({{ bundleApplyModeLabel }}).
              <template v-if="bundleMaxDiscountValue > 0">
                Trần giảm: {{ convertPrice(bundleMaxDiscountValue) }}.
              </template>
              <template v-if="bundleMaxDiscountedQty > 0">
                Tối đa {{ bundleMaxDiscountedQty }} món được tính giảm.
              </template>
            </span>
          </div>
        </b-alert>
        <b-row class="mb-4">
          <b-col md="4">
            <b-input
              size="sm"
              placeholder="Tìm kiếm"
              v-model="searchText"
              append-icon="search"
              single-line
              hide-details
              v-on:keyup.enter="onFilter()"
            ></b-input>
          </b-col>
          <b-col md="1">
            <b-button
              style="fontweight: 600; width: 70px"
              variant="primary"
              size="sm"
              @click="onFilter"
              >Lọc</b-button
            >
          </b-col>
        </b-row>
        <b-table
          :items="listItem"
          :fields="fields"
          :busy="onLoading"
          bordered
          hover
          show-empty
          class="product-table"
        >
          <template #empty="scope">
            <h5 class="text-center">
              Không có sản phẩm bán kèm nào được gợi ý
            </h5>
          </template>
          <template v-slot:head(selected)>
            <span>
              <b-form-checkbox
                v-model="isCheckAll"
                size="lg"
                @change="clickAll"
              ></b-form-checkbox>
            </span>
          </template>
          <template v-slot:cell(selected)="row">
            <div class="d-flex justify-content-center">
              <b-form-checkbox
                size="lg"
                v-model="row.item.checked"
                @change="clickIndex()"
              ></b-form-checkbox>
            </div>
          </template>
          <template v-slot:cell(id)="row">
            <div>
              <span
                v-text="row.item.id"
                class="d-flex justify-content-center"
              ></span>
            </div>
          </template>
          <template v-slot:cell(productName)="row">
            <div style="display: flex; gap: 8px; align-items: center">
              <ProductImage :src="row.item.productImageUrl" />
              <div>
                <span
                  v-text="row.item.barCode"
                  style="font-size: 12px"
                  v-show="row.item.barCode"
                ></span
                ><br />
                <span
                  v-text="row.item.productCode"
                  style="font-size: 13px"
                  v-show="row.item.productCode"
                ></span
                ><br />
                <span
                  v-text="row.item.productName"
                  style="white-space: normal; color: #3699ff; font-size: 13px"
                ></span>
              </div>
            </div>
          </template>
          <template v-slot:cell(discountValue)="row">
            <div>
              {{ convertPrice(row.item.discountValue) }}
              ({{ DISCOUNT_AMOUNT_TYPE_NAME[row.item.discountType] }})
            </div>
          </template>
        </b-table>
        <b-button
          style="fontweight: 600; width: 100px"
          variant="primary"
          size="sm"
          :disabled="onSubmitting"
          @click="submit"
          >Áp dụng</b-button
        >
        <b-button
          style="margin-left: 10px; font-weight: 600; width: 70px"
          variant="secondary"
          size="sm"
          @click="hideModal"
        >
          Hủy
        </b-button>
        <div v-if="isBundleMode" class="mt-3 text-muted">
          <span>Đã chọn: {{ selectedAccessoryQty }} phụ kiện</span>
          <span class="mx-2">|</span>
          <span>Tổng trước giảm: {{ convertPrice(selectedAccessoryAmount) }}</span>
          <span class="mx-2">|</span>
          <span>Giảm tạm tính: {{ convertPrice(totalBundleDiscount) }}</span>
          <span class="mx-2">|</span>
          <span>Tổng sau giảm: {{ convertPrice(selectedFinalAmount) }}</span>
        </div>
      </v-form>
    </b-modal>
  </div>
</template>

<style scoped>
.product-table /deep/ th {
  text-align: center;
  font-weight: 600;
  color: '#181c32';
}
</style>

<script>
import { convertPrice, xoa_dau } from '@/utils/common';
import { BILL_ITEM_TYPE } from '@/utils/enum';
import { DISCOUNT_AMOUNT_TYPE_NAME } from '@/utils/enum-name';
import { v4 } from 'uuid';
import { cloneDeep } from 'lodash';
import ApiService from '@/core/services/api.service';
import ProductImage from '@/view/components/products/ProductImage.vue';

const MONEY_DISCOUNT_TYPE = 1;
const PERCENT_DISCOUNT_TYPE = 2;
const ROUND_UNIT = 1000;

export default {
  props: {
    billItemId: [String, Number],
    productName: String,
    storeId: [String, Number],
    productId: [String, Number],
    attachDepth: {
      type: Number,
      default: 1,
    },
    mainProductId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      DISCOUNT_AMOUNT_TYPE_NAME,
      modalTitle: '',
      searchText: '',
      listItem: [],
      listItemDraff: [],
      fields: [
        {
          key: 'selected',
          label: '',
          sortable: false,
        },
        {
          key: 'productName',
          label: 'Sản phẩm',
          sortable: false,
        },
        {
          key: 'quantityInStock',
          label: 'Tồn',
          sortable: false,
          tdClass: 'text-right',
        },
        {
          key: 'sellingPrice',
          label: 'Giá bán',
          sortable: false,
          tdClass: 'text-right',
          formatter: (value) => {
            return convertPrice(value);
          },
        },
        {
          key: 'discountValue',
          label: 'Chiết khấu',
          sortable: false,
          tdClass: 'text-right',
        },
        {
          key: 'finalSellingPrice',
          label: 'Giá chiết khấu',
          sortable: false,
          tdClass: 'text-right',
          formatter: (value) => {
            return convertPrice(value);
          },
        },
        { key: 'promotionProgramCode', label: 'Mã BPK' },
      ],
      isCheckAll: false,
      onLoading: false,
      onSubmitting: false,
      promotionInfo: null,
      apiWarnings: [],
    };
  },
  components: {
    ProductImage,
  },
  computed: {
    isBundleMode() {
      return this.promotionInfo?.promotionMode === 'BUNDLE_DISCOUNT';
    },
    bundleRule() {
      return this.promotionInfo?.bundleRule || {};
    },
    bundleMinQty() {
      return Number(
        this.bundleRule.minAccessoryQty || this.bundleRule.minimumAccessoryQty || 2,
      );
    },
    bundleDiscountType() {
      const discountTypeValue =
        this.bundleRule.discountType || this.bundleRule.bundleDiscountType;
      if (String(discountTypeValue).toUpperCase() === 'PERCENT') {
        return PERCENT_DISCOUNT_TYPE;
      }
      if (String(discountTypeValue).toUpperCase() === 'MONEY') {
        return MONEY_DISCOUNT_TYPE;
      }
      return Number(discountTypeValue || MONEY_DISCOUNT_TYPE);
    },
    bundleDiscountValuePerApply() {
      return Number(
        this.bundleRule.discountValue || this.bundleRule.bundleDiscountValue || 0,
      );
    },
    bundleApplyMode() {
      return this.bundleRule.applyMode || 'ONCE';
    },
    bundleMaxDiscountValue() {
      return Number(
        this.bundleRule.maxDiscountValue || this.bundleRule.bundleMaxDiscountValue || 0,
      );
    },
    bundleMaxDiscountedQty() {
      return Number(
        this.bundleRule.maxDiscountedQty || this.bundleRule.bundleMaxDiscountedQty || 0,
      );
    },
    bundleDiscountDisplay() {
      if (
        this.bundleDiscountType === PERCENT_DISCOUNT_TYPE ||
        String(this.bundleRule.discountType).toUpperCase() === 'PERCENT'
      ) {
        return `${this.bundleDiscountValuePerApply}%`;
      }
      return convertPrice(this.bundleDiscountValuePerApply);
    },
    bundleApplyModeLabel() {
      return this.bundleApplyMode === 'MULTIPLE'
        ? 'áp dụng theo bội số'
        : 'áp dụng 1 lần';
    },
    selectedItems() {
      return this.listItem.filter((item) => item.checked);
    },
    selectedAccessoryQty() {
      return this.selectedItems.reduce(
        (total, item) => total + Number(item.quantity || 1),
        0,
      );
    },
    selectedAccessoryAmount() {
      return this.selectedItems.reduce((total, item) => {
        const quantity = Number(item.quantity || 1);
        const sellingPrice = Number(item.sellingPrice || 0);
        return total + quantity * sellingPrice;
      }, 0);
    },
    effectiveSelectedItems() {
      if (!this.bundleMaxDiscountedQty || this.bundleMaxDiscountedQty <= 0) {
        return this.selectedItems;
      }
      const sortedItems = [...this.selectedItems].sort((a, b) => {
        const priceA = Number(a.sellingPrice || 0);
        const priceB = Number(b.sellingPrice || 0);
        return priceB - priceA;
      });
      return sortedItems.slice(0, this.bundleMaxDiscountedQty);
    },
    effectiveAccessoryQty() {
      return this.effectiveSelectedItems.reduce(
        (total, item) => total + Number(item.quantity || 1),
        0,
      );
    },
    effectiveAccessoryAmount() {
      return this.effectiveSelectedItems.reduce((total, item) => {
        const quantity = Number(item.quantity || 1);
        const sellingPrice = Number(item.sellingPrice || 0);
        return total + quantity * sellingPrice;
      }, 0);
    },
    applyTimes() {
      if (!this.isBundleMode || this.effectiveAccessoryQty < this.bundleMinQty) {
        return 0;
      }
      if (this.bundleApplyMode === 'MULTIPLE') {
        return Math.floor(this.effectiveAccessoryQty / this.bundleMinQty);
      }
      return 1;
    },
    totalBundleDiscount() {
      if (!this.isBundleMode || this.applyTimes <= 0) {
        return 0;
      }
      const baseDiscount =
        this.bundleDiscountType === PERCENT_DISCOUNT_TYPE ||
        String(this.bundleRule.discountType).toUpperCase() === 'PERCENT'
          ? (this.effectiveAccessoryAmount * this.bundleDiscountValuePerApply) / 100
          : this.bundleDiscountValuePerApply;
      const discountRaw = Number(baseDiscount) * Number(this.applyTimes);
      const discountByMaxValue =
        this.bundleMaxDiscountValue > 0
          ? Math.min(discountRaw, this.bundleMaxDiscountValue)
          : discountRaw;
      const roundedDiscount = this.roundToThousand(discountByMaxValue);
      return Math.min(roundedDiscount, this.effectiveAccessoryAmount);
    },
    selectedFinalAmount() {
      return this.selectedAccessoryAmount - this.totalBundleDiscount;
    },
    resolvedMainProductId() {
      return this.mainProductId || this.productId;
    },
  },
  methods: {
    convertPrice,
    roundToThousand(value = 0) {
      return Math.round(Number(value || 0) / ROUND_UNIT) * ROUND_UNIT;
    },
    showModal: function () {
      this.searchText = '';
      this.promotionInfo = null;
      this.apiWarnings = [];
      this.isCheckAll = false;
      this.fetchProductAttach();
      const depthLabel =
        Number(this.attachDepth) === 2 ? ' <span class="text-muted">(BPK cấp 2)</span>' : '';
      this.modalTitle = `<div>
              <span class='d-block' style='font-size: 1.1rem'>Thêm sản phẩm bán kèm cho sản phẩm: <a class='text-primary'>#${this.productName}</a>${depthLabel}</span>
              </div>`;
      this.$refs['bill-attach-modal'].show();
    },
    hideModal: function () {
      this.$refs['bill-attach-modal'].hide();
    },
    clickAll() {
      this.listItem.forEach((item) => {
        item.checked = this.isCheckAll;
      });
    },
    clickIndex() {
      this.isCheckAll = this.listItem.every((item) => item.checked);
    },
    onFilter() {
      const textSearch = this.searchText ? this.searchText.trim() : '';
      this.listItem = this.listItemDraff
        .filter((item) => {
          const itemName = item.productName || item.name || '';
          const nameNoneSign = xoa_dau(itemName);
          return (
            nameNoneSign.toLowerCase().indexOf(textSearch.toLowerCase()) > -1 ||
            itemName.toLowerCase().indexOf(textSearch.toLowerCase()) > -1
          );
        })
        .slice(0, 10);
    },
    async submit() {
      const listSelected = this.listItem.filter((item) => item.checked);
      if (!listSelected.length) {
        this.hideModal();
        return;
      }

      let listResult = listSelected;
      if (this.isBundleMode) {
        listResult = await this.resolveBundleDiscount(listSelected);
      }

      const attachLevel = Number(this.attachDepth) || 1;
      listResult = listResult.map((item) => ({
        ...item,
        attachLevel,
      }));

      this.$emit('addAttach', listResult);
      this.hideModal();
    },
    async resolveBundleDiscount(selectedItems) {
      if (!this.promotionInfo?.id) {
        return this.applyBundleDiscount(selectedItems);
      }
      this.onSubmitting = true;
      try {
        const { data } = await ApiService.post('v1/product-promotion/attach/calculate', {
          promotionId: this.promotionInfo.id,
          productId: this.productId,
          storeId: this.storeId,
          selectedAccessoryItems: selectedItems.map((item) => ({
            productId: item.productId,
            qty: Number(item.quantity || 1),
            unitPrice: Number(item.sellingPrice || 0),
          })),
        });
        const payload = data?.data || data || {};
        const allocation = payload.allocation || [];
        if (!allocation.length) {
          return this.applyBundleDiscount(selectedItems);
        }
        const allocationByProduct = {};
        allocation.forEach((row) => {
          allocationByProduct[String(row.productId || row.id)] = row;
        });
        return selectedItems.map((item) => {
          const alloc =
            allocationByProduct[String(item.productId)] ||
            allocation.find((row) => String(row.id) === String(item.id));
          if (!alloc) {
            return item;
          }
          const qty = Number(item.quantity || 1);
          const lineDiscount = Number(alloc.discountAmount || 0);
          const finalAmount = Number(alloc.finalAmount ?? alloc.finalSellingPrice ?? 0);
          return {
            ...item,
            discountType: MONEY_DISCOUNT_TYPE,
            discountValue: lineDiscount,
            finalSellingPrice: qty > 0 ? finalAmount / qty : finalAmount,
          };
        });
      } catch (error) {
        return this.applyBundleDiscount(selectedItems);
      } finally {
        this.onSubmitting = false;
      }
    },
    applyBundleDiscount(selectedItems = []) {
      if (!selectedItems.length) {
        return selectedItems;
      }

      const targetItems = this.effectiveSelectedItems;
      const targetItemIds = new Set(targetItems.map((item) => item.id));
      const totalAmount = targetItems.reduce((total, item) => {
        const quantity = Number(item.quantity || 1);
        const sellingPrice = Number(item.sellingPrice || 0);
        return total + quantity * sellingPrice;
      }, 0);
      const totalDiscount = Math.min(this.totalBundleDiscount, totalAmount);
      if (totalDiscount <= 0) {
        return selectedItems.map((item) => ({
          ...item,
          discountType: MONEY_DISCOUNT_TYPE,
          discountValue: 0,
          finalSellingPrice: Number(item.sellingPrice || 0),
        }));
      }

      let discountAllocated = 0;
      return selectedItems.map((item) => {
        if (!targetItemIds.has(item.id)) {
          return {
            ...item,
            discountType: MONEY_DISCOUNT_TYPE,
            discountValue: 0,
            finalSellingPrice: Number(item.sellingPrice || 0),
          };
        }
        const quantity = Number(item.quantity || 1);
        const sellingPrice = Number(item.sellingPrice || 0);
        const lineAmount = quantity * sellingPrice;
        const targetIndex = targetItems.findIndex((target) => target.id === item.id);
        const isLastTarget = targetIndex === targetItems.length - 1;

        let lineDiscount = 0;
        if (isLastTarget) {
          lineDiscount = totalDiscount - discountAllocated;
        } else if (totalAmount > 0) {
          lineDiscount = this.roundToThousand(
            (lineAmount / totalAmount) * totalDiscount,
          );
          discountAllocated += lineDiscount;
        }

        lineDiscount = Math.min(lineDiscount, lineAmount);
        const lineFinalAmount = Math.max(lineAmount - lineDiscount, 0);

        return {
          ...item,
          discountType: MONEY_DISCOUNT_TYPE,
          discountValue: lineDiscount,
          finalSellingPrice: lineFinalAmount,
        };
      });
    },
    fetchProductAttach() {
      this.onLoading = true;
      ApiService.query(`v1/product-promotion/attach`, {
        params: {
          productId: this.productId,
          storeId: this.storeId,
          attachDepth: this.attachDepth || 1,
          mainProductId: this.resolvedMainProductId,
        },
      })
        .then(({ data }) => {
          const payload = data.data || {};
          const responseData = Array.isArray(payload)
            ? payload
            : payload.items || [];
          this.promotionInfo = Array.isArray(payload)
            ? null
            : payload.promotion || payload.program || null;
          this.apiWarnings = Array.isArray(payload.warnings) ? payload.warnings : [];
          this.listItem = responseData.map((row) => {
            return {
              ...row,
              id: v4(),
              checked: false,
              itemType: BILL_ITEM_TYPE.PRODUCT_PROMTION,
              belongBillDetailId: this.billItemId,
              promotionProgramId:
                row.promotionProgramId || row.accessoryGroupId || this.promotionInfo?.id,
              accessoryGroupId: row.accessoryGroupId || row.promotionProgramId,
              accessoryGroupCode:
                row.accessoryGroupCode || row.promotionProgramCode || this.promotionInfo?.code,
            };
          });
          this.listItemDraff = cloneDeep(this.listItem);
          this.onLoading = false;
        })
        .catch(() => {
          this.onLoading = false;
        });
    },
  },
};
</script>
